import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NvButton } from "@/components/navigator/nv-button";
import { NvBookmarkButton } from "@/components/navigator/nv-bookmark-button";
import { NvPill } from "@/components/navigator/nv-pill";
import { NvBadge } from "@/components/navigator/nv-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import bookmarkIcon from "@/assets/chat/bookmark.svg";
import type { Need } from "@/services/assistant-service";
import { insightFields, type InsightField } from "../insight-fields";

export interface AssistantInsight {
  needs: string;
  bottleneck: string;
  environment: string;
  scope: string;
  demoUrl?: string;
  items?: Need[];
  unknown?: string[];
  fields?: InsightField[];
}
export interface AssistantAgent {
  id: string;
  name: string;
  description: string;
  supplier_name?: string | null;
  certified?: boolean;
  logo_url?: string | null;
  conceptual?: boolean;
  dev_stage?: string | null;
  level?: number;
  type: string;
  bookmarked?: boolean;
  rationale?: string;
  confirmed?: string[];
  unknown?: string[];
  confirmed_labels?: string[];
  unknown_labels?: string[];
  has_definition?: boolean;
  demo_url?: string | null;
}
export interface AssistantResultsProps {
  resultsUnavailable?: boolean;
  insight?: AssistantInsight;
  agents?: AssistantAgent[];
  exploratory?: boolean;
  requiredComplete?: boolean;
  resultStatus?: "waiting" | "ready" | "error";
  resultVersion?: number;
  searching?: boolean;
  degraded?: boolean;
  demo?: { name: string; url: string | null };
  hasConversation: boolean;
  onContact?: () => void;
  onViewAgent?: (agent: AssistantAgent) => void;
  onViewDefinition?: (agent: AssistantAgent) => void;
  onInquire?: (agent: AssistantAgent) => void;
  onBookmark?: (agent: AssistantAgent) => void;
  onMore?: () => void;
  onRetrySearch?: () => void;
  bookmarkPending?: string | null;
}

export function AssistantResults({
  insight,
  agents = [],
  exploratory = false,
  requiredComplete = false,
  resultStatus = "waiting",
  resultVersion,
  searching,
  degraded = false,
  demo,
  hasConversation,
  onContact,
  onViewAgent,
  onViewDefinition,
  onInquire,
  onBookmark,
  bookmarkPending,
  onRetrySearch,
}: AssistantResultsProps) {
  const [page, setPage] = useState(0);
  const [insightExpanded, setInsightExpanded] = useState(true);
  const candidateIds = agents.map((agent) => agent.id).join(",");
  useEffect(() => setPage(0), [resultVersion, exploratory, candidateIds]);
  const pages = Math.ceil(agents.length / 3);
  const activePage = Math.min(page, Math.max(0, pages - 1));
  const pagination = pages > 1 && (
    <div
      aria-label="추천 에이전트 페이지"
      className="flex items-center gap-2 text-[12px] text-nv-text-secondary"
    >
      <NvButton
        variant="secondary"
        size="sm"
        className="h-6 w-6 p-0"
        aria-label="이전 추천 페이지"
        disabled={activePage === 0}
        onClick={() => setPage(activePage - 1)}
      >
        ‹
      </NvButton>
      <span>
        {activePage + 1} / {pages}
      </span>
      <NvButton
        variant="secondary"
        size="sm"
        className="h-6 w-6 p-0"
        aria-label="다음 추천 페이지"
        disabled={activePage + 1 >= pages}
        onClick={() => setPage(activePage + 1)}
      >
        ›
      </NvButton>
    </div>
  );
  const items =
    insight?.items ??
    (insight
      ? [
          {
            needs: insight.needs,
            bottleneck: insight.bottleneck,
            environment: [insight.environment],
            work: "",
            goal: "",
          },
        ]
      : []);
  const fields =
    insight?.fields ??
    insightFields(
      insight
        ? {
            needs: items,
            scope: insight.scope,
            unknown: insight.unknown ?? [],
            requirements: [],
          }
        : undefined,
    );
  return (
    <aside
      aria-label="상담 분석 및 추천"
      className="flex h-full min-h-0 min-w-0 flex-col gap-5 overflow-hidden bg-nv-bg-page p-4 lg:p-6"
    >
      <div
        className={cn(
          "relative min-h-0",
          insightExpanded ? "flex-1" : "shrink-0",
        )}
      >
        <Card
          id="assistant-insight-panel"
          className="flex h-full min-h-0 flex-col overflow-hidden rounded-nv-card border-nv-border shadow-none"
        >
          <CardHeader
            className={cn("shrink-0 p-6", insightExpanded && "pb-3.5")}
          >
            <CardTitle className="text-[15px] font-medium leading-[18px]">
              Insight Extraction
            </CardTitle>
          </CardHeader>
          <CardContent
            aria-label="추출된 상담 정보"
            tabIndex={0}
            className={cn(
              "min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-6 pt-0",
              insightExpanded ? "flex" : "hidden",
            )}
          >
            {fields.length > 0 || demo ? (
              <div className="grid min-w-0 gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(240px,440px)]">
                <div className="min-w-0 space-y-[18px]">
                  {fields.map(({ key, label, value }) => (
                    <section key={key} className="space-y-1.5">
                      <h3 className="text-[15px] font-medium leading-[18px]">
                        {label}
                      </h3>
                      <p className="break-words text-[14px] leading-[22px] text-nv-text-secondary">
                        · {value}
                      </p>
                    </section>
                  ))}
                  {insight?.scope && (
                    <NvPill
                      tone="highlight"
                      className="border border-nv-brand/30 bg-nv-brand-subtle px-[10px] py-[6px] text-[13px] font-bold leading-4 text-nv-text-primary"
                    >
                      {insight.scope}
                    </NvPill>
                  )}
                </div>
                <div
                  className={cn(
                    "flex min-w-0 items-center justify-center rounded-nv-card bg-nv-bg-subtle text-center text-[13px] text-nv-text-secondary",
                    demo ? "min-h-[248px]" : "h-12",
                  )}
                >
                  {demo?.url ? (
                    <video
                      key={demo.url}
                      controls
                      preload="metadata"
                      src={demo.url}
                      aria-label={`${demo.name} 데모 영상`}
                      className="max-h-[248px] w-full rounded-nv-card"
                    />
                  ) : (
                    <p className="px-4">
                      {demo
                        ? `${demo.name}의 등록된 데모 영상이 없습니다.`
                        : "▶ 데모 영상 · 추천 에이전트를 선택하면 열려요"}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex min-h-28 flex-1 items-center justify-center rounded-nv-card border border-dashed border-nv-border p-4 text-center text-[13px] text-nv-text-secondary">
                {hasConversation
                  ? "대화에서 확인된 정보가 이곳에 표시됩니다"
                  : "대화가 시작되면 진단 내용이 정리됩니다"}
              </div>
            )}
          </CardContent>
          {insightExpanded && (hasConversation || insight) && (
            <div className="flex shrink-0 justify-end px-6 py-3">
              <NvButton onClick={onContact} disabled={!onContact}>
                Contact PwC
              </NvButton>
            </div>
          )}
        </Card>
        <NvButton
          variant="secondary"
          size="sm"
          className="absolute bottom-0 left-1/2 z-10 h-7 w-7 -translate-x-1/2 translate-y-1/2 rounded-full border-nv-border p-0"
          aria-label={
            insightExpanded
              ? "Insight Extraction 접기"
              : "Insight Extraction 펼치기"
          }
          aria-expanded={insightExpanded}
          aria-controls="assistant-insight-panel"
          onClick={() => setInsightExpanded((value) => !value)}
        >
          {insightExpanded ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
        </NvButton>
      </div>
      <section
        id="agent-candidates-panel"
        aria-labelledby="agent-candidates-title"
        className={cn(
          "min-h-0 space-y-3 overflow-y-auto",
          insightExpanded ? "max-h-[45%] shrink-0" : "flex-1",
        )}
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h2
              id="agent-candidates-title"
              className="text-[15px] font-medium leading-[18px]"
            >
              Agent 추천 결과 – {requiredComplete ? "적합 후보" : "탐색 후보"}
            </h2>
            {(!requiredComplete || !exploratory) && pagination}
          </div>
          {requiredComplete && exploratory && resultStatus === "ready" && (
            <p className="text-[13px] leading-5 text-nv-text-secondary">
              필수 조건을 모두 충족하는 적합 후보가 없어요. 아래 탐색 후보를
              확인해 주세요.
            </p>
          )}
        </div>
        {degraded && (
          <p role="status" className="text-[13px] text-nv-text-secondary">
            일부 검색·조건 확인이 원활하지 않아 확인 가능한 결과를 표시하고
            있어요. 잠시 후 다시 확인해 주세요.
          </p>
        )}
        <>
          {requiredComplete && exploratory && resultStatus === "ready" && (
            <>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-[15px] font-medium">
                  Agent 추천 결과 – 탐색 후보
                </h3>
                {pagination}
              </div>
            </>
          )}
          {agents.length ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {agents.slice(activePage * 3, activePage * 3 + 3).map((agent) => (
                <Tooltip key={agent.id} delayDuration={200}>
                  <TooltipTrigger asChild>
                    <Card
                      tabIndex={0}
                      aria-label={`${agent.name} 추천 카드`}
                      className="flex min-w-0 flex-col gap-2 rounded-nv-card border-nv-border px-4 py-3.5 shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <h3
                          className="truncate text-[15px] font-medium leading-[18px]"
                          title={agent.name}
                        >
                          {agent.name}
                        </h3>
                        {agent.type && (
                          <NvPill className="max-w-[50%] truncate px-[10px] py-1 text-[11px] font-medium leading-[13px]">
                            {agent.type}
                          </NvPill>
                        )}
                        <NvBookmarkButton
                          bookmarked={!!agent.bookmarked}
                          disabled={!onBookmark || bookmarkPending === agent.id}
                          ariaLabel={`${agent.name} 북마크`}
                          onToggle={() => onBookmark?.(agent)}
                          icon={<img src={bookmarkIcon} alt="" />}
                          size="sm"
                          className="ml-auto disabled:opacity-40"
                        />
                      </div>
                      {(agent.supplier_name ||
                        agent.certified ||
                        agent.conceptual) && (
                        <div className="flex flex-wrap items-center gap-2 text-[12px] text-nv-text-secondary">
                          {agent.supplier_name && (
                            <span>{agent.supplier_name}</span>
                          )}
                          {agent.certified && <NvBadge>PwC Certified</NvBadge>}
                          {agent.conceptual && <NvPill>컨셉추얼</NvPill>}
                        </div>
                      )}
                      <p className="truncate text-[12px] leading-[14px] text-nv-text-secondary">
                        {agent.description}
                      </p>
                      <p className="text-[11px] font-medium leading-[13px]">
                        추천 근거: {agent.rationale || "등록 정보 기준"}
                      </p>
                      <div className="flex flex-wrap gap-1.5 text-[10.5px] font-medium leading-[13px]">
                        {Array.from(
                          new Set(agent.confirmed_labels ?? agent.confirmed),
                        ).map((value) => (
                          <span
                            key={value}
                            className="rounded-nv-pill bg-[#dbf2e5] px-1.5 py-0.5 text-[#057340]"
                          >
                            확인 · {value}
                          </span>
                        ))}
                        {Array.from(
                          new Set(agent.unknown_labels ?? agent.unknown),
                        ).map((value) => (
                          <span
                            key={value}
                            className="rounded-nv-pill bg-[#f5f5f7] px-1.5 py-0.5 text-[#595961]"
                          >
                            미확인 · {value}
                          </span>
                        ))}
                      </div>
                      <div className="mt-auto flex flex-wrap items-center justify-between gap-2">
                        <div className="flex gap-3 text-[12px] font-bold underline underline-offset-2">
                          <button
                            onClick={() => onViewAgent?.(agent)}
                            disabled={!onViewAgent}
                          >
                            기본정보
                          </button>
                          <button
                            onClick={() => onViewDefinition?.(agent)}
                            disabled={
                              !onViewDefinition || !agent.has_definition
                            }
                            className="disabled:opacity-40"
                          >
                            정의서
                          </button>
                        </div>
                        <NvButton
                          size="sm"
                          onClick={() => onInquire?.(agent)}
                          disabled={!onInquire}
                        >
                          도입 문의
                        </NvButton>
                      </div>
                    </Card>
                  </TooltipTrigger>
                  {agent.description && (
                    <TooltipContent className="max-w-[min(360px,calc(100vw-32px))] break-words md:max-w-[360px]">
                      {agent.description}
                    </TooltipContent>
                  )}
                </Tooltip>
              ))}
            </div>
          ) : (
            <Card
              role="status"
              aria-live="polite"
              className="rounded-nv-card border-dashed border-nv-border px-4 py-6 text-center text-[13px] leading-4 text-nv-text-secondary shadow-none"
            >
              {searching
                ? "조건에 맞는 에이전트를 찾고 있습니다…"
                : resultStatus === "error"
                  ? "검색 서비스를 이용할 수 없습니다. 대화에서 다시 요청하거나 Contact PwC를 이용해 주세요."
                  : resultStatus === "ready"
                    ? "조건에 맞는 후보가 없습니다. 추가 조건을 알려 주세요."
                    : "대기 중 · 업무와 해결하려는 문제가 확인되면 후보가 표시돼요"}
              {resultStatus === "error" && (
                <NvButton
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  onClick={onRetrySearch}
                  disabled={!onRetrySearch || searching}
                >
                  다시 검색
                </NvButton>
              )}
            </Card>
          )}
        </>
      </section>
    </aside>
  );
}
